package one.healthium.profile.ui.food.models

import loco.GENERAL_UNIT_CUP_LABEL
import loco.GENERAL_UNIT_FL_OZ_LABEL
import loco.GENERAL_UNIT_GRAM_LABEL
import loco.GENERAL_UNIT_MEAL_LABEL
import loco.GENERAL_UNIT_ML_LABEL
import loco.GENERAL_UNIT_OZ_LABEL
import loco.GENERAL_UNIT_PIECE_LABEL
import loco.GENERAL_UNIT_TBSP_LABEL
import loco.GENERAL_UNIT_TSP_LABEL
import one.healthium.base.utils.Formatters.formatCup
import one.healthium.base.utils.Formatters.formatFlOz
import one.healthium.base.utils.Formatters.formatGrams
import one.healthium.base.utils.Formatters.formatMeal
import one.healthium.base.utils.Formatters.formatMl
import one.healthium.base.utils.Formatters.formatOz
import one.healthium.base.utils.Formatters.formatPiece
import one.healthium.base.utils.Formatters.formatTbsp
import one.healthium.base.utils.Formatters.formatTsp
import one.healthium.domain.common.FoodUnit
import one.healthium.loco.LString
import one.healthium.loco.asLString

sealed interface Units {
    val displayValue: LString
    val gramValue: Float
    val isFloating: Boolean

    fun format(value: Float): LString

    object Gram : Units {
        override val displayValue: LString
            get() = loco.Res.string.GENERAL_UNIT_GRAM_LABEL.asLString()
        override val gramValue: Float
            get() = 1f
        override val isFloating = false

        override fun format(value: Float): LString = value.formatGrams()
    }

    object Ml : Units {
        override val displayValue: LString
            get() = loco.Res.string.GENERAL_UNIT_ML_LABEL.asLString()
        override val gramValue: Float
            get() = 1f
        override val isFloating = false

        override fun format(value: Float): LString = value.formatMl()
    }

    object Oz : Units {
        override val displayValue: LString
            get() = loco.Res.string.GENERAL_UNIT_OZ_LABEL.asLString()
        override val gramValue: Float
            get() = 28.3495f
        override val isFloating = true

        override fun format(value: Float): LString = value.formatOz()
    }

    object FlOz : Units {
        override val displayValue: LString
            get() = loco.Res.string.GENERAL_UNIT_FL_OZ_LABEL.asLString()
        override val gramValue: Float
            get() = 29.5735f
        override val isFloating = true

        override fun format(value: Float): LString = value.formatFlOz()
    }

    data class Tablespoon(
        override val gramValue: Float
    ) : Units {
        override val displayValue: LString
            get() = loco.Res.string.GENERAL_UNIT_TBSP_LABEL.asLString()
        override val isFloating = true

        override fun format(value: Float): LString = value.formatTbsp()
    }

    data class Teaspoon(
        override val gramValue: Float
    ) : Units {
        override val displayValue: LString
            get() = loco.Res.string.GENERAL_UNIT_TSP_LABEL.asLString()
        override val isFloating = true

        override fun format(value: Float): LString = value.formatTsp()
    }

    data class Cup(
        override val gramValue: Float
    ) : Units {
        override val displayValue: LString
            get() = loco.Res.string.GENERAL_UNIT_CUP_LABEL.asLString()
        override val isFloating = true

        override fun format(value: Float): LString = value.formatCup()
    }

    data class Piece(
        override val gramValue: Float
    ) : Units {
        override val displayValue: LString
            get() = loco.Res.string.GENERAL_UNIT_PIECE_LABEL.asLString()
        override val isFloating = true

        override fun format(value: Float): LString = value.formatPiece()
    }

    data class Portion(
        override val gramValue: Float
    ) : Units {
        override val displayValue: LString
            get() = loco.Res.string.GENERAL_UNIT_MEAL_LABEL.asLString()
        override val isFloating = true

        override fun format(value: Float): LString = value.formatMeal()
    }
}

fun Units.toFoodUnit(): FoodUnit {
    return when (this) {
        is Units.Cup -> FoodUnit.Cup
        Units.FlOz -> FoodUnit.FlOz
        Units.Gram -> FoodUnit.Gram
        Units.Ml -> FoodUnit.Ml
        Units.Oz -> FoodUnit.Oz
        is Units.Piece -> FoodUnit.Piece
        is Units.Tablespoon -> FoodUnit.Tablespoon
        is Units.Teaspoon -> FoodUnit.Teaspoon
        is Units.Portion -> FoodUnit.Portion
    }
}
